import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useValidateVendorToken, useRegisterVendor, useAcceptVendorInvitation } from '@api/hooks/useVendorPortal';
import { useAuthStore } from '@store/authStore';
import Button from '@components/common/Button';
import { Alert } from '@components/common/Alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/common/Card';
import { Input } from '@components/common/Input';
import { LoadingSpinner } from '@components/common/LoadingSpinner';

export default function VendorRegisterPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const { isAuthenticated } = useAuthStore();
  const { data: validation, isLoading: validating } = useValidateVendorToken(token);
  const registerMutation = useRegisterVendor();
  const acceptMutation = useAcceptVendorInvitation();

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
  });
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAccept = async () => {
    try {
      await acceptMutation.mutateAsync(token);
      navigate('/vendor-customers');
    } catch {
      // The mutation renders and notifies the API error.
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      await registerMutation.mutateAsync({
        token,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email || validation?.email || '',
        password: form.password,
      });
      navigate('/auth?registered=vendor');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-page p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <CardTitle>Missing Invitation</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription>No invitation token found. Please use the link from your invitation email.</CardDescription>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (validating) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-page p-4">
        <LoadingSpinner text="Validating invitation..." />
      </div>
    );
  }

  if (!validation?.valid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-page p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Invitation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-content-secondary">
              {validation?.message || 'This invitation link is invalid or has expired.'}
            </p>
            <p className="text-sm text-content-muted">
              Please contact your customer to request a new invitation.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already logged in - show accept UI instead of registration form
  if (isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-page p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>New Customer Invitation</CardTitle>
            <CardDescription>
              <span className="font-medium">{validation?.customer_name}</span> has invited you to connect your vendor account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {acceptMutation.isError && (
              <Alert variant="danger">
                {(acceptMutation.error as any)?.response?.data?.detail || 'Failed to accept invitation'}
              </Alert>
            )}
            <Button
              type="button"
              variant="primary"
              disabled={acceptMutation.isPending}
              className="w-full"
              onClick={handleAccept}
            >
              {acceptMutation.isPending ? 'Connecting...' : `Connect to ${validation?.customer_name ?? 'Customer'}`}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-page p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Register as a Vendor</CardTitle>
          <CardDescription>
            <span className="font-medium">{validation.customer_name}</span> has invited you to connect on EZ Flow.
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              name="first_name"
              type="text"
              label="First Name"
              required
              value={form.first_name}
              onChange={handleChange}
            />
            <Input
              name="last_name"
              type="text"
              label="Last Name"
              required
              value={form.last_name}
              onChange={handleChange}
            />
          </div>

          <Input
            name="email"
            type="email"
            label="Email"
            required
            value={form.email || validation.email || ''}
            onChange={handleChange}
          />

          <Input
            name="password"
            type="password"
            label="Password"
            required
            minLength={8}
            value={form.password}
            onChange={handleChange}
          />

          <Input
            name="confirm_password"
            type="password"
            label="Confirm Password"
            required
            value={form.confirm_password}
            onChange={handleChange}
          />

          {error && (
            <Alert variant="danger">{error}</Alert>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={registerMutation.isPending}
            className="w-full"
          >
            {registerMutation.isPending ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <p className="mt-4 text-center text-sm text-content-muted">
          Already have an account?{' '}
          <a
            href={`/auth?redirect=${encodeURIComponent(`/register/vendor?token=${token}`)}`}
            className="link-primary"
          >
            Sign in
          </a>
        </p>
      </Card>
    </div>
  );
}
